import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { signInFailureMessage } from '@evogard/domain';
import { Alert, Button, Field, Wordmark, linkTextClasses } from '@evogard/ui';
import { auth } from '../../lib/auth.js';
import { CONTEXT_KEY } from '../../lib/session.js';

export function SignInScreen() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [erro, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  async function signIn(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSending(true);

    try {
      const { error } = await auth.signIn.email({ email, password: password });
      if (error) {
        // A mensagem é a mesma para e-mail inexistente e senha errada: dizer
        // qual dos dois falhou entrega quem tem conta na casa.
        //
        // O que ela NÃO pode fazer é chamar de senha errada o que não é. Com o
        // banco fora, a API devolve 500 — e a tela dizendo "senha incorreta"
        // faz a pessoa desconfiar de uma senha certa e pedir troca de senha por
        // causa de um container parado. A classificação está em
        // @evogard/domain, com teste.
        setError(signInFailureMessage(error.status, error.code));
        return;
      }

      // `reset` e não `invalidate`: o guard da rota usa ensureQueryData, que
      // devolveria o `null` em cache (marcado como velho, mas ainda lá) e mandaria de volta para cá.
      await client.resetQueries({ queryKey: CONTEXT_KEY });
      void navigate({ to: '/', replace: true });
    } catch {
      // O cliente do Better Auth **rejeita** quando o fetch não chega a receber
      // resposta — API fora do ar, offline, DNS. Sem este catch a promessa
      // morre aqui: o botão fica em "Enviando…" para sempre e a tela não diz
      // nada. Era o pior dos dois defeitos, porque não havia o que ler.
      setError(signInFailureMessage(undefined));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-sm">
        {/*
         * A marca no tamanho de marca. `text-lg` deixava a entrada com um
         * título menor que o de qualquer tela interna — e esta é a única tela
         * que a pessoa vê antes de saber onde está.
         */}
        <div className="mb-6">
          <Wordmark size="capa" level="h1" subtitle="Plataforma" surface="superficie" />
          <p className="mt-3 text-sm text-texto-suave">
            Entre para acessar a plataforma.
          </p>
        </div>

        <form
          onSubmit={(e) => void signIn(e)}
          className="flex flex-col gap-4 rounded-borda border border-borda bg-superficie p-5"
        >
          {erro && <Alert tone="erro">{erro}</Alert>}

          <Field
            label="E-mail"
            type="email"
            autoComplete="username"
            // A tela existe para isto e não tem outra coisa para fazer.
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label="Senha"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button type="submit" loading={sending} className="mt-1">
            Entrar
          </Button>
        </form>

        <Link to="/forgot-password" className={linkTextClasses('mt-4 text-sm')}>
          Esqueci minha senha
        </Link>
      </div>
    </div>
  );
}
